/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect } from 'react';

import { css, cssMap, jsx, keyframes } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onReadMoreButtonClicked } from '../../../ai-answer-dialog/on-read-more-button-clicked';
import { onReadMoreButtonShown } from '../../../ai-answer-dialog/on-read-more-button-shown';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';

import { messages } from './messages';

enum UpdatedDarkThemeStops {
	ORANGE = '#FCA700',
	GREEN = '#82B536',
	BLUE = '#357DE8',
	PURPLE = '#BF63F3',
}

enum UpdatedLightThemeStops {
	ORANGE = '#FCA700',
	GREEN = '#6A9A23',
	BLUE = '#1868DB',
	PURPLE = '#AF59E0',
}

// Helper function to convert hex color to rgba with alpha
const hexToRgba = (hex: string, alpha: number): string => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// This injects a CSS property into the document for the --gradient-angle variable.
// This is needed so the browser understands how to animate the gradient-angle as an angle
// value. This is done this way because compiled doesn't support CSS variables at this time.
let propertyInjected = false;
const useGradientAngleProperty = () => {
	useEffect(() => {
		if (propertyInjected) {
			return;
		}
		propertyInjected = true;

		try {
			const style = document.createElement('style');
			style.innerHTML = `
				@property --gradient-angle {
					syntax: '<angle>';
					initial-value: 0deg;
					inherits: false;
				}
			`;
			document.head.appendChild(style);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (e) {
			// ignore: if this fails, the border will still have the gradient, but it will just not animate
		}
	}, []);
};

const unboundedStyles = cssMap({
	root: {
		position: 'relative',
	},
	lineStyles: {
		height: '1px',
	},
	lineNotStreamingStyles: {
		backgroundColor: token('color.border'),
	},
	showMoreButtonWrapperStyles: {
		position: 'absolute',
		inset: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	showMoreButtonStyles: {
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		width: '130px',
		height: '32px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'&:hover': {
			cursor: 'pointer',
		},
	},
});

// Keyframes for rotating the gradient angle
// ts-ignore because jira build is failing, but this is legit
// @ts-ignore
const rotateGradient = keyframes({
	// @ts-ignore
	from: {
		'--gradient-angle': '0deg',
	},
	// @ts-ignore
	to: {
		'--gradient-angle': '360deg',
	},
});

const lineAnimation = css({
	animation: `${rotateGradient} 2s linear infinite`,
});

const lightModeAnimatedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle) at -50% 50%, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 0deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 45deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 1)} 90deg, ${hexToRgba(UpdatedLightThemeStops.GREEN, 1)} 135deg, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 1)} 180deg, ${hexToRgba(UpdatedLightThemeStops.ORANGE, 1)} 225deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 1)} 270deg, ${hexToRgba(UpdatedLightThemeStops.PURPLE, 1)} 315deg, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 360deg)`,
});

const darkModeAnimatedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	background: `conic-gradient(from var(--gradient-angle) at -50% 50%, ${hexToRgba(UpdatedLightThemeStops.BLUE, 1)} 0deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 1)} 45deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 1)} 90deg, ${hexToRgba(UpdatedDarkThemeStops.GREEN, 1)} 135deg, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 1)} 180deg, ${hexToRgba(UpdatedDarkThemeStops.ORANGE, 1)} 225deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 1)} 270deg, ${hexToRgba(UpdatedDarkThemeStops.PURPLE, 1)} 315deg, ${hexToRgba(UpdatedDarkThemeStops.BLUE, 1)} 360deg)`,
});

type AnimatedSingleLineShowMoreProps = {
	isStreaming: boolean;
	showMoreButtonVisible: boolean;
	/** When provided, controls whether onReadMoreButtonShown fires. Use to decouple event firing
	 *  from button visibility — e.g. fire only when text content is truncated, not due to other content. */
	fireShownEvent?: boolean;
	onClickShowMoreButton?: () => void;
};

export const AnimatedSingleLineShowMore: ({
	isStreaming,
	showMoreButtonVisible,
	fireShownEvent,
	onClickShowMoreButton,
}: AnimatedSingleLineShowMoreProps) => JSX.Element = ({
	isStreaming,
	showMoreButtonVisible,
	fireShownEvent,
	onClickShowMoreButton,
}: AnimatedSingleLineShowMoreProps) => {
	useGradientAngleProperty();
	const { colorMode } = useThemeObserver();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { commonAttributes } = useAIAnswerContext();

	// Fire analytics event when button is shown
	// to keep in parity with old read more button - used for SSR
	useEffect(() => {
		const shouldFire = fireShownEvent !== undefined ? fireShownEvent : showMoreButtonVisible;
		if (shouldFire) {
			fireAnalyticsEvent(onReadMoreButtonShown(commonAttributes));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fireShownEvent, showMoreButtonVisible]);

	const handleClick = useCallback(() => {
		fireAnalyticsEvent(onReadMoreButtonClicked(commonAttributes));
		if (!fg('kd_fix_sain_mau_instrumentation')) {
			fireSAINUsageEvent({
				metCriteria: SainAction.READ_MORE_BUTTON_CLICKED,
				commonAttributes,
			});
		}

		onClickShowMoreButton?.();
	}, [fireAnalyticsEvent, fireSAINUsageEvent, onClickShowMoreButton, commonAttributes]);

	return (
		<div css={unboundedStyles.root}>
			<div
				css={[
					unboundedStyles.lineStyles,
					lineAnimation,
					isStreaming
						? colorMode === 'dark'
							? darkModeAnimatedStyles
							: lightModeAnimatedStyles
						: unboundedStyles.lineNotStreamingStyles,
				]}
			/>
			{showMoreButtonVisible && (
				<div css={unboundedStyles.showMoreButtonWrapperStyles}>
					<button css={unboundedStyles.showMoreButtonStyles} onClick={handleClick}>
						<Inline alignBlock="center" space="space.100">
							<ChevronDownIcon label={''} />
							<Text color="color.text.subtle" weight="medium">
								<FormattedMessage {...messages.ai_answer_card_show_more_button} />
							</Text>
						</Inline>
					</button>
				</div>
			)}
		</div>
	);
};
